import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE, SOCIALS, OFFICES } from "../data/site";

const upsert = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const Seo = ({ title, description, schemas = [], image, hiPath }) => {
    const { pathname } = useLocation();
    const origin = SITE.url;
    const path = pathname.endsWith("/") ? pathname : `${pathname}/`;
    const canonical = `${origin}${path}`;

    useEffect(() => {
        document.title = title;
        upsert("name", "description", description);
        upsert("property", "og:title", title);
        upsert("property", "og:description", description);
        upsert("property", "og:url", canonical);
        upsert("property", "og:locale", "en_IN");
        upsert("name", "twitter:title", title);
        upsert("name", "twitter:description", description);
        upsert("name", "twitter:card", "summary_large_image");
        if (image) {
            upsert("property", "og:image", image);
            upsert("name", "twitter:image", image);
        }
        let link = document.head.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement("link");
            link.setAttribute("rel", "canonical");
            document.head.appendChild(link);
        }
        link.setAttribute("href", canonical);

        const extras = [];
        if (hiPath) {
            [
                ["en-IN", `${origin}${path}`],
                ["hi-IN", `${origin}${hiPath}`],
            ].forEach(([lang, href]) => {
                const el = document.createElement("link");
                el.rel = "alternate";
                el.hreflang = lang;
                el.href = href;
                document.head.appendChild(el);
                extras.push(el);
            });
        }

        const scripts = schemas.map((s) => {
            const el = document.createElement("script");
            el.type = "application/ld+json";
            el.dataset.seo = "page";
            el.text = JSON.stringify(s);
            document.head.appendChild(el);
            return el;
        });
        return () => [...scripts, ...extras].forEach((s) => s.remove());
    }, [title, description, canonical, image, hiPath, schemas, origin, path]);

    return null;
};

export const breadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.label,
        item: `${SITE.url}${it.to}`,
    })),
});

export const faqSchema = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
});

export const orgSchemas = () => {
    const origin = SITE.url;
    return [
        {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: SITE.name,
            url: origin,
            sameAs: [
                "https://www.instagram.com/ominfra.yeida",
                "https://www.facebook.com/ominfra.yeida",
                "https://www.linkedin.com/company/ominfra-yeida",
                "https://www.youtube.com/@ominfra-yeida",
            ],
            logo: `${origin}/assets/om-infra-logo.jpg`,
            description:
                "YEIDA Authority plots and semi commercial (7% Kisan Kota) plot specialists on the Yamuna Expressway near Jewar Airport",
            foundingDate: "2013",
            sameAs: SOCIALS.map((s) => s.url),
            contactPoint: {
                "@type": "ContactPoint",
                telephone: "+919311305205",
                contactType: "sales",
                email: "officialominfra@gmail.com",
                areaServed: ["IN"],
                availableLanguage: ["English", "Hindi"],
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: SITE.name,
            description: SITE.tagline,
            url: origin,
            sameAs: [
                "https://www.instagram.com/ominfra.yeida",
                "https://www.facebook.com/ominfra.yeida",
                "https://www.linkedin.com/company/ominfra-yeida",
                "https://www.youtube.com/@ominfra-yeida",
            ],
            telephone: "+919311305205",
            email: SITE.email,
            address: {
                "@type": "PostalAddress",
                streetAddress: SITE.address,
                addressRegion: "Uttar Pradesh",
                addressCountry: "IN",
            },
            location: OFFICES.map((o) => ({
                "@type": "Place",
                name: o.name,
                address: o.address,
                geo: { "@type": "GeoCoordinates", latitude: o.geo.lat, longitude: o.geo.lng },
            })),
            areaServed: ["Greater Noida", "Noida", "Yamuna Expressway", "Jewar", "Gautam Buddha Nagar"],
            openingHours: "Mo-Sa 09:30-18:30",
            priceRange: "₹₹",
        },
    ];
};

export default Seo;
